import type { ReceiptStatus } from "@opensea/graphql"
import { useNumberFormatter } from "@opensea/i18n"
import {
  FlexCenter,
  TextBody,
  Toast,
  ToastAvatar,
  ToastContent,
  ToastDescription,
  ToastErrorIcon,
  ToastSide,
  ToastSuccessIcon,
  ToastTitle,
} from "@opensea/ui-kit"
import { StacksFilled, TraitOffer } from "@opensea/ui-kit/icons"
import { useTranslations } from "use-intl"
import { Avatar } from "../../../components/Avatar"
import { useRenderImage } from "../../../providers/RenderImageProvider"

type CreateCollectionOfferToastProps = {
  imageUrl: string
  price: {
    unit: number
    symbol: string
  }
  trait?: {
    traitType: string
    value: string
  }
  traits?: {
    traitType: string
    value: string
  }[]
  status?: ReceiptStatus
}

export function CreateCollectionOfferToast({
  imageUrl,
  price,
  trait,
  traits,
  status,
}: CreateCollectionOfferToastProps) {
  const t = useTranslations("wallet.CreateCollectionOfferToast")
  const { renderImage } = useRenderImage()
  const format = useNumberFormatter()
  const hasTraits = Boolean(trait || traits?.length)

  return (
    <Toast>
      <ToastAvatar
        badge={
          <Avatar.Badge>
            {hasTraits ? <TraitOffer size={12} /> : <StacksFilled size={12} />}
          </Avatar.Badge>
        }
        renderImage={renderImage}
        src={imageUrl || ""}
      />
      <ToastContent>
        {status === "SUCCESS" && (
          <>
            <ToastTitle>
              <FlexCenter className="gap-1 text-nowrap">
                {hasTraits
                  ? t("traitOfferSuccessTitle")
                  : t("collectionOfferSuccessTitle")}{" "}
                {hasTraits ? (
                  <TextBody color="text-secondary" weight="regular">
                    {trait
                      ? `${trait.traitType}: ${trait.value}`
                      : t("traits", { count: traits?.length ?? 0 })}
                  </TextBody>
                ) : null}
              </FlexCenter>
            </ToastTitle>
            <ToastDescription>
              {format(price.unit, {
                display: "full",
                suffix: price.symbol,
              })}
            </ToastDescription>
          </>
        )}
        {status === "FAILED" && (
          <ToastTitle>
            {hasTraits
              ? t("traitOfferFailedTitle")
              : t("collectionOfferFailedTitle")}
          </ToastTitle>
        )}
      </ToastContent>
      <ToastSide>
        {status === "SUCCESS" && <ToastSuccessIcon />}
        {status === "FAILED" && <ToastErrorIcon />}
      </ToastSide>
    </Toast>
  )
}
